import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, FormControlLabel, Skeleton, Switch, Tooltip } from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import { formatDateTime, toTitleCase } from 'common/utils';
import useGlobalStyles from 'styles/globalStyles';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import { tableColumnProps } from 'common/constants';
import { useGetDevices } from 'hooks/useDevices';
import BlockDeviceActionButton from 'components/tables/ActionButtons/BlockDeviceActionButton';
import { EUserStatus } from 'enum';

interface DeviceAccount {
  id: string;
  fullName: string;
  status: EUserStatus;
  phone: string | null;
}

// One row per phone the app has been used on, the last seen first, with the
// accounts on it - so blocking it is judged knowing who else it would catch.
export default function DevicesTable() {
  const globalStyles = useGlobalStyles();
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const requestParams = {
    page: page + 1,
    size,
    blocked: blockedOnly,
  };

  const { data, refetch, isLoading } = useGetDevices(requestParams, {
    onError: () => console.log('error'),
  });

  const accountLine = (account: DeviceAccount) =>
    `${toTitleCase(account.fullName || '—')}${account.phone ? ` (+${account.phone})` : ''}${account.status === EUserStatus.BLOCKED ? ' - blocked' : ''}`;

  const columns: GridColDef[] = [
    {
      field: 'device',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text='Device' />,
      renderCell: (params) => (
        <CustomCellValue text={[params.row.brand, params.row.model].filter(Boolean).join(' ') || '—'} />
      ),
    },
    {
      field: 'system',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text='System' />,
      renderCell: (params) => (
        <CustomCellValue text={[params.row.platform, params.row.osVersion].filter(Boolean).join(' ') || '—'} />
      ),
    },
    {
      field: 'accounts',
      ...tableColumnProps,
      flex: 2,
      renderHeader: () => <CustomCellLabel text='Accounts' />,
      // The latest account, and how many more - all of them on hover.
      renderCell: (params) => {
        const accounts: DeviceAccount[] = params.row.accounts ?? [];
        if (!accounts.length) return <CustomCellValue text='None' />;
        return (
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{accounts.map(accountLine).join('\n')}</Box>}>
            <Box>
              <CustomCellValue text={`${accountLine(accounts[0])}${accounts.length > 1 ? `  +${accounts.length - 1} more` : ''}`} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'appVersion',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <CustomCellLabel text='App' />,
      renderCell: (params) => <CustomCellValue text={params.row.appVersion ? `v${params.row.appVersion}` : '—'} />,
    },
    {
      field: 'lastSeenAt',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text='Last seen' />,
      renderCell: (params) => <CustomCellValue text={formatDateTime(params.row.lastSeenAt)} />,
    },
    {
      field: 'status',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <CustomCellLabel text='Status' />,
      // Who blocked it, when and why, on hover.
      renderCell: (params) => {
        const blocked = !!params.row.blockedAt;
        const why = blocked
          ? [
            `Blocked ${formatDateTime(params.row.blockedAt)}`,
            params.row.blockedBy?.fullName && `by ${params.row.blockedBy.fullName}`,
            params.row.blockReason,
          ].filter(Boolean).join('\n')
          : '';
        return (
          <Tooltip title={why ? <Box sx={{ whiteSpace: 'pre-line' }}>{why}</Box> : ''}>
            <Box>
              <CustomText text={blocked ? 'Blocked' : 'Active'} color={blocked ? colors.error : colors.success} fontSize={14} fontWeight={600} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <CustomCellLabel text='Actions' />,
      renderCell: (params) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <BlockDeviceActionButton device={params.row} refetch={refetch} />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <PhoneAndroidIcon sx={{ color: colors.primary, fontSize: 22 }} />
            </Box>
            <CustomText text="Devices" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>

          <FormControlLabel
            control={<Switch checked={blockedOnly} onChange={(e) => { setBlockedOnly(e.target.checked); setPage(0); }} />}
            label='Blocked only'
          />
        </Box>


        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>

      <Box mt={3} />
    </Container>
  );
}
